// FarmSet（wire）→ FarmInput（base 値・エンジン入力）へ変換する橋渡し層。
// base 換算し、未取得レート行を警告して除外する（§4）。グループは独立展開せず
// EngineGroup（確定 m 個・カテゴリカル抽選）としてエンジンに渡す（§5.2.2）。
import { rowBaseValue, toBase } from '../currency/convert';
import type { RateTable } from '../currency/rate-table';
import { resolveRate } from '../core/rate-model';
import type { EngineDropRow, EngineGroup, FarmInput, ResolvedRate } from '../core/types';
import { isGroupDrop } from '../store/schema';
import type { DropEntry, FarmSet, GroupMember, RateMode } from '../store/schema';

export interface ExpandResult {
  input: FarmInput;
  /** 未取得レートで計算から除外した行の説明（§4: 黙って0にしない）。 */
  warnings: string[];
  /** 警告に出た通貨 id（重複なし）。 */
  missingCurrencies: string[];
}

/** メンバーの相対重み（比）。rateMode があればその点推定、無ければ入力値 p。 */
export function memberWeight(member: GroupMember): number {
  if (member.rateMode) return Math.max(0, resolveRate(member.rateMode).lambdaHat);
  return Math.max(0, member.p);
}

/** 行の価格参照キー（自動=itemId / 手動=unit）。警告表示用。 */
function priceRef(row: { itemId?: string; unit: string }): string {
  return row.itemId ?? row.unit;
}

export function expandSet(set: FarmSet, rt: RateTable): ExpandResult {
  const warnings: string[] = [];
  const missing = new Set<string>();
  const flag = (currencyId: string, message: string) => {
    warnings.push(message);
    missing.add(currencyId);
  };

  // コスト合計 C。未取得のコスト行は除外でなく計算全体の無効化（§4.1: 除外は EV 過大＝危険側）。
  let C = 0;
  let costIncomplete = false;
  for (const line of set.costLines) {
    const per = rowBaseValue(line, rt);
    if (per == null) {
      const ref = priceRef(line);
      flag(ref, `コスト「${line.name}」: 「${ref}」のレートが未取得のため計算不能（手動価格に切替えるか相場を再取得）`);
      costIncomplete = true;
      continue;
    }
    C += per * line.count;
  }

  // 安定収入 S。
  let Sbase = 0;
  const sb = toBase(set.steady.amount, set.steady.unit, rt);
  if (sb == null) {
    if (set.steady.amount !== 0) {
      flag(set.steady.unit, `安定収入: 「${set.steady.unit}」のレートが未取得のため計算から除外`);
    }
  } else {
    Sbase = sb;
  }

  // ドロップ行（通常行）とグループ（確定 m 個・カテゴリカル抽選）を分けて構築。
  const drops: EngineDropRow[] = [];
  const groups: EngineGroup[] = [];
  for (const entry of set.dropLines) {
    appendDropEntry(entry, rt, drops, groups, flag);
  }

  return {
    input: { C, Sbase, t: set.time, drops, groups, ...(costIncomplete ? { costIncomplete } : {}) },
    warnings,
    missingCurrencies: [...missing],
  };
}

function appendDropEntry(
  entry: DropEntry,
  rt: RateTable,
  out: EngineDropRow[],
  groupsOut: EngineGroup[],
  flag: (currencyId: string, message: string) => void,
): void {
  if (isGroupDrop(entry)) {
    // 確定 m 個グループ: エンジンで「ちょうど m 個・比でカテゴリカル抽選」する（独立展開しない）。
    // 価格未取得のメンバーは pBase=0 で残す（抽選枠は消費するが価値は付かない）。
    // 警告は通貨単位で集約する（§4.1: メンバー数十件で同一警告を並べない）。
    const missingByCur = new Map<string, number>();
    const members = entry.members.map((member) => {
      const pBase = rowBaseValue(member, rt);
      if (pBase == null) {
        const ref = priceRef(member);
        missingByCur.set(ref, (missingByCur.get(ref) ?? 0) + 1);
      }
      return { pBase: pBase ?? 0, weight: memberWeight(member) };
    });
    for (const [ref, count] of missingByCur) {
      flag(ref, `グループ「${entry.name}」: ${count} メンバーの価値が未取得（「${ref}」）のため 0 扱い`);
    }
    groupsOut.push({ m: Math.max(0, entry.m), members });
    return;
  }
  const pBase = rowBaseValue(entry, rt);
  if (pBase == null) {
    const ref = priceRef(entry);
    flag(ref, `ドロップ「${entry.name}」: 「${ref}」のレートが未取得のため計算から除外`);
    return;
  }
  out.push({ pBase, rate: resolveDropRate(entry.rateMode, entry.count) });
}

/** direct モードは count（λ表示値）を真とする。 */
function resolveDropRate(mode: RateMode, count: number): ResolvedRate {
  if (mode.kind === 'direct') return { kind: 'fixed', lambda: Math.max(0, count) };
  return resolveRate(mode).resolved;
}
